<script lang="ts">
	// One bundle serves two audiences on two paths.
	//
	// / is the till a cashier stands at. /owner is the dashboard the owner reads
	// on a laptop. They share a build because they share an origin, a stylesheet
	// and a message catalogue, and nothing else.
	//
	// Neither side decides what to show from the path. The owner area asks
	// /api/owner/accounts/me and the till asks /api/till/me and /api/till/sessions/me,
	// because every cookie here is HttpOnly and the server's answer is the only
	// honest one.

	import { languageChoice, resolvedTheme, setSurface } from './display.svelte';
	import Pair from './screens/Pair.svelte';
	import Lock from './screens/Lock.svelte';
	import Till from './screens/Till.svelte';
	import SignIn from './screens/SignIn.svelte';
	import CreateAccount from './screens/CreateAccount.svelte';
	import EmailLink from './screens/EmailLink.svelte';
	import ResetLink from './screens/ResetLink.svelte';
	import ForgotPassword from './screens/ForgotPassword.svelte';
	import CreateRestaurant from './screens/CreateRestaurant.svelte';
	import Dashboard from './screens/Dashboard.svelte';
	import Button from './components/Button.svelte';
	import Loading from './components/Loading.svelte';
	import { directionOf, langTag, languageOf, message, type Language } from './i18n/messages';
	import {
		isOwnerPath,
		replacePath,
		currentPath,
		EMAIL_LINK_PATH,
		FORGOT_PATH,
		RESET_LINK_PATH,
		SIGN_IN_METHODS_PATH,
		SIGN_UP_PATH,
	} from './router.svelte';
	import {
		session,
		loadSession,
		refreshSession,
		signOut,
		type Account,
	} from './owner/session.svelte';
	import { till, loadTill, paired, opened, closed } from './till/session.svelte';
	import TillSuspended from './screens/TillSuspended.svelte';
	import TillRemoved from './screens/TillRemoved.svelte';
	import { removal, watchStoreState } from './till/storeState.svelte';
	import ShellNotice from './screens/ShellNotice.svelte';
	import OwnerSuspended from './screens/OwnerSuspended.svelte';
	import { bundleReady, inShell } from './till/shell';
	import { followStore, storeStatus } from './till/storeStatus.svelte';
	import StoreDown from './screens/StoreDown.svelte';
	import { api } from './api/client';
	import { BUNDLE_COMMIT, watchBuild } from './build.svelte';
	import { onMount, untrack } from 'svelte';
	import { clearSuspension, suspension } from './suspension.svelte';
	import { pendingUndo, undo } from './owner/notices.svelte';

	const owner = $derived(isOwnerPath());
	const view = $derived(session());
	const here = $derived(till());

	// The till runs only inside the Bosla app, whose shell relays its calls to
	// the till's own order store (#371). A plain browser has no road to the
	// API, so its till page shows the notice and asks nothing (D21). The shell
	// is there from the page's first script, or never.
	const shell = inShell();

	// Which side this page is, so the language and theme it reads are that
	// side's own: set before the first paint, and again if the path moves.
	setSurface(isOwnerPath() ? 'owner' : 'till');
	$effect(() => setSurface(owner ? 'owner' : 'till'));

	// The restaurant's language, once there is a restaurant to ask.
	const accountLanguage = $derived.by((): Language | null => {
		if (!owner && (here.status === 'locked' || here.status === 'open')) {
			return languageOf(here.till.settings.language);
		}
		if (owner && view.status === 'ready') {
			return languageOf(view.account.settings.language);
		}
		return null;
	});

	// This screen's own choice wins; until it makes one it follows the
	// restaurant's. Before there is a restaurant (a till never paired, an
	// owner not signed in) it is Arabic: the MVP and its first year sell only
	// in Egypt, and the create form makes Arabic the default for a new one.
	const language = $derived<Language>(languageChoice() ?? accountLanguage ?? 'ar');

	$effect(() => {
		document.documentElement.lang = langTag(language);
		document.documentElement.dir = directionOf(language);
	});

	// On the root, so the lock screen, the pairing screen and the dashboard
	// all follow it, and theme.css restates every token beneath it.
	$effect(() => {
		document.documentElement.dataset.theme = resolvedTheme();
	});

	// Each side asks only its own questions. A till has no owner session and no
	// reason to send an unauthenticated request on every boot, and the dashboard
	// is not a till.
	$effect(() => {
		if (owner && view.status === 'loading') void loadSession();
	});
	$effect(() => {
		if (!owner && shell && here.status === 'loading') void loadTill();
	});

	// Inside the Bosla app the shell keeps the till's order store running and
	// says when it is starting or down; the till then covers itself and offers
	// no sale (T22), and carries on when the store answers again (#392). A
	// first read that found the store not yet up is read again then.
	$effect(() => {
		if (!owner && shell) return followStore();
	});
	const store = $derived(shell && !owner ? storeStatus() : 'up');
	// Once, as the store comes up: a read that fails while it is up stays
	// failed, with its Retry, and is never asked again in a loop.
	let wasUp = false;
	$effect(() => {
		const up = store === 'up';
		const cameUp = up && !wasUp;
		wasUp = up;
		if (cameUp && !owner && shell && untrack(() => here.status) === 'failed') void loadTill();
	});

	// The page has rendered its first screen: it tells the shell, with its own
	// commit, so a screen bundle on trial is kept (#392). A bundle whose page
	// never gets here is rolled back within 60 seconds. A call the shell did
	// not answer is made again, a few times, well inside that minute.
	onMount(() => {
		if (!shell) return;
		let tries = 0;
		const tell = () => {
			bundleReady(BUNDLE_COMMIT).catch(() => {
				tries += 1;
				if (tries < 5) setTimeout(tell, 5_000);
			});
		};
		tell();
	});

	// Put the URL where the screen already is, so an owner who reloads or
	// bookmarks it lands back on what they were looking at.
	$effect(() => {
		if (view.status === 'noRestaurant' && currentPath() !== '/owner/create') {
			if (!onLinkPage()) replacePath('/owner/create');
		}
		// Creating an account and asking for a reset are for someone signed
		// out; once signed in, the dashboard is where they are.
		if (
			view.status === 'ready' &&
			(currentPath() === SIGN_UP_PATH || currentPath() === FORGOT_PATH)
		) {
			replacePath('/owner');
		}
	});

	// Signed in by an email and a password, or by a mailed link: the session
	// cookie is set, so ask again who this is, from the dashboard's own path.
	//
	// The sign-up link's page also adds a password from Sign-in methods
	// (UC-28), and goes back there. A sign-up's new owner has no restaurant,
	// so the effect above moves them on to the form that creates one.
	function ownerSignedIn() {
		if (currentPath() === EMAIL_LINK_PATH) replacePath(SIGN_IN_METHODS_PATH);
		else if (onLinkPage() || currentPath() === SIGN_UP_PATH) replacePath('/owner');
		void loadSession();
	}

	// A mailed link's page, which acts whoever is signed in.
	function onLinkPage(): boolean {
		return currentPath() === EMAIL_LINK_PATH || currentPath() === RESET_LINK_PATH;
	}

	// Bosla suspended the account (UC-23). The till covers whatever it shows;
	// the dashboard shows only the suspended screen.
	const suspended = $derived(suspension());
	let checking = $state(false);

	const tillWhere = $derived(
		here.status === 'locked' || here.status === 'open'
			? `${here.till.account} · ${here.till.shop} · ${here.till.till}`
			: '',
	);

	// Asks the server at once. A paired till asks which till it is, which
	// leaves the screen beneath as it was; a till that never loaded loads; a
	// till still pairing has nothing to ask with, so its next try asks.
	async function checkTill() {
		checking = true;
		try {
			if (here.status === 'locked' || here.status === 'open') {
				await api.GET('/api/till/me');
			} else if (here.status === 'unpaired') {
				clearSuspension();
			} else {
				await loadTill();
			}
		} catch {
			// Still covered: the message stays as it was.
		} finally {
			checking = false;
		}
	}

	// Reloads the dashboard, and opens Home once Bosla has resumed it.
	async function checkOwner() {
		checking = true;
		await loadSession();
		checking = false;
		if (!suspension()) replacePath('/owner');
	}

	// The dashboard reads its restaurant again every hour, and whenever its tab
	// comes back into view, so a dashboard left open sees the subscription
	// warning start, count down and turn to "ended" (UC-24), not the days left
	// it showed when it loaded.
	$effect(() => {
		if (!owner || view.status !== 'ready') return;
		const again = () => void refreshSession();
		const onShow = () => {
			if (document.visibilityState === 'visible') again();
		};
		const timer = setInterval(again, 60 * 60 * 1000);
		document.addEventListener('visibilitychange', onShow);
		return () => {
			clearInterval(timer);
			document.removeEventListener('visibilitychange', onShow);
		};
	});

	// A dashboard left open learns of a newer bundle (INT-7) and loads it at
	// its next navigation (router.svelte.ts). The till learns from its menu.
	$effect(() => {
		if (owner) return watchBuild();
	});

	// The suspended screen replaces the dashboard, and with it the Undo of a
	// removal still waiting to be sent. The server would refuse that removal
	// now anyway, so it is put back rather than lost unseen: when Bosla resumes
	// the account the row is where the owner can see it.
	$effect(() => {
		if (owner && suspended && pendingUndo()) undo();
	});

	// The till reads its order store's state from its first screen on (#382,
	// N14): the waiting count, the link to Bosla, and a removal, whose cover
	// sits above every other till screen, T13 included (N16).
	$effect(() => {
		if (!owner && shell) return watchStoreState();
	});
	const removed = $derived(!owner && shell ? removal() : null);

	// Signing out of the suspended screen leaves for the sign-in screen; the
	// suspension belongs to the account, not to the next person to sign in.
	async function leaveSuspended() {
		await signOut();
		clearSuspension();
	}

	function onCreated(_account: Account) {
		// Replace rather than push: pressing back into the create form after the
		// restaurant exists would show a form the server now refuses.
		replacePath('/owner');
	}
</script>

{#if !owner && !shell}
	<ShellNotice {language} />
{:else if !owner}
	<div class="till" inert={suspended !== null || store !== 'up' || removed !== null}>
		{#if here.status === 'loading'}
			<section class="screen" data-testid="screen-loading">
				<div class="arrive">
					<Loading
						size="lg"
						label={message(language, 'loading')}
						caption={message(language, 'loading')}
					/>
				</div>
			</section>
		{:else if here.status === 'unpaired'}
			<Pair {language} onPaired={paired} />
		{:else if here.status === 'locked'}
			<Lock {language} till={here.till} onUnlocked={opened} />
		{:else if here.status === 'open'}
			<Till
				{language}
				till={here.till}
				session={here.session}
				idleSeconds={here.till.settings.idleLockSeconds}
				onSignedOut={closed}
			/>
		{:else}
			<section class="screen" data-testid="screen-load-failed">
				<p>{message(language, 'loadFailed')}</p>
				<Button
					testId="reload-till"
					label={message(language, 'retry')}
					onPress={() => void loadTill()}
				/>
			</section>
		{/if}
	</div>
	{#if store !== 'up'}
		<div class="till">
			<StoreDown {language} status={store} />
		</div>
	{/if}
	{#if suspended}
		<div class="till" inert={removed !== null}>
			<TillSuspended
				{language}
				where={tillWhere}
				contact={suspended}
				{checking}
				onCheck={() => void checkTill()}
			/>
		</div>
	{/if}
	{#if removed}
		<div class="till">
			<TillRemoved
				{language}
				where={tillWhere}
				waiting={removed.waiting}
				openOrders={removed.openOrders}
			/>
		</div>
	{/if}
{:else if currentPath() === EMAIL_LINK_PATH}
	<EmailLink {language} onSignedIn={ownerSignedIn} />
{:else if currentPath() === RESET_LINK_PATH}
	<ResetLink {language} onSignedIn={ownerSignedIn} />
{:else if suspended}
	<OwnerSuspended
		{language}
		restaurant={view.status === 'ready' ? view.account.name : ''}
		contact={suspended}
		{checking}
		onCheck={() => void checkOwner()}
		onSignOut={() => void leaveSuspended()}
	/>
{:else if view.status === 'loading'}
	<section class="screen" data-testid="screen-loading">
		<div class="arrive">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	</section>
{:else if view.status === 'signedOut' && currentPath() === SIGN_UP_PATH}
	<CreateAccount {language} />
{:else if view.status === 'signedOut' && currentPath() === FORGOT_PATH}
	<ForgotPassword {language} />
{:else if view.status === 'signedOut'}
	<SignIn {language} onSignedIn={ownerSignedIn} />
{:else if view.status === 'noRestaurant'}
	<CreateRestaurant {language} {onCreated} />
{:else if view.status === 'ready'}
	<Dashboard {language} account={view.account} />
{:else}
	<section class="screen" data-testid="screen-load-failed">
		<p>{message(language, 'loadFailed')}</p>
		<Button
			testId="reload-session"
			label={message(language, 'retry')}
			onPress={() => void loadSession()}
		/>
	</section>
{/if}

<style>
	/* The till's root. The design system sizes every control from --ctl-min,
	 * 36 px for the mouse-first back office, and a touch surface raises it to
	 * --tap-min once, here, rather than through a prop on every component.
	 * display: contents keeps the wrapper out of the layout; the property
	 * still inherits through it. */
	.till {
		display: contents;
		--ctl-min: var(--tap-min);
	}

	/* A wait shows only after 300 ms: a load that finishes sooner flashes
	 * nothing. bosla-rise is the design's own "something arriving". */
	.arrive {
		animation: bosla-rise var(--dur-enter) var(--ease-out) 300ms both;
	}
</style>
